// src/pages/Dashboard.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { isAuthenticated } from '../services/authService';
import LoadingState from '../components/common/LoadingState';
import { useLanguage } from '../contexts/LanguageContext';
import AchievementSuggestions from '../components/achievements/AchievementSuggestions';
import { SuggestionCategory, getAchievementSuggestions, createSuggestionsFromGroups } from '../services/achievementSuggestionService';
import { getGroupedAchievements } from '../services/achievementService';
import SyncProgressButton from '../components/achievements/SyncProgressButton';

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<SuggestionCategory[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState<boolean>(false);
  const { t } = useLanguage();

  // Function to load achievement suggestions
  const loadSuggestions = useCallback(async () => {
    if (!authenticated) return;
    
    try {
      setSuggestionsLoading(true);
      
      // Try to get suggestions from server
      const suggestionsData = await getAchievementSuggestions();

      
      // If we got suggestions from the server, use them
      if (suggestionsData.length) {
        setSuggestions(suggestionsData);
      } else {
        try {
          const groupsData = await getGroupedAchievements();
          const generatedSuggestions = createSuggestionsFromGroups(groupsData);

          setSuggestions(generatedSuggestions);
        } catch (fallbackError) {
          console.error('Failed to create fallback suggestions:', fallbackError);
          setSuggestions([]);
        }
      }
    } catch (error) {
      console.error('Error loading suggestions:', error);
      // Empty suggestions in case of error
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [authenticated]);

  useEffect(() => {
    const checkAuth = () => {
      const isAuth = isAuthenticated();
      setAuthenticated(isAuth);
      return isAuth;
    };
    
    const isAuth = checkAuth();
    if (isAuth) {
      loadSuggestions();
    }
    
    setLoading(false);
    
    // Event listener for auth state changes
    const handleAuthChange = () => {
      const newAuthState = checkAuth();
      if (newAuthState) {
        loadSuggestions();
      }
    };
    
    // Event listener for progress sync changes
    const handleProgressSync = () => {
      if (authenticated) {
        loadSuggestions();
      }
    };
    
    window.addEventListener('auth-state-changed', handleAuthChange);
    window.addEventListener('progress-sync-completed', handleProgressSync);
    
    return () => {
      window.removeEventListener('auth-state-changed', handleAuthChange);
      window.removeEventListener('progress-sync-completed', handleProgressSync);
    };
  }, [authenticated, loadSuggestions]);

  if (loading) {
    return <LoadingState message={t('common.loading')} />;
  }

  return (
    <div>
      {/* Welcome area */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-destiny-gold">
            {authenticated ? t('dashboard.welcomeBack') : t('dashboard.welcome')}
          </h2>
          
          {authenticated && (
            <SyncProgressButton compact={true} onSyncComplete={loadSuggestions} />
          )}
        </div>
        
        <p className="text-gray-300 mb-4">
          {authenticated
            ? t('dashboard.useNavigation')
            : t('dashboard.intro')}
        </p>
        
        {!authenticated && (
          <Link
            to="/login"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-destiny-blue hover:bg-opacity-90"
          >
            {t('auth.signInWithBungie')}
          </Link>
        )}
      </div>

      {/* Achievement suggestions - Only for authenticated users */}
      {authenticated && (
        <>
          {suggestionsLoading ? (
            <LoadingState message={t('achievements.loadingSuggestions')} />
          ) : (
            suggestions.length > 0 && (
              <AchievementSuggestions 
                categories={suggestions} 
                userIsAuthenticated={authenticated} 
              />
            )
          )}
          
          {/* Additional action area */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-white mb-4">{t('dashboard.browseAchievements')}</h3>
              <p className="text-gray-300 mb-4">{t('dashboard.browseDescription')}</p>
              <Link
                to="/achievements?reset=1"
                className="inline-flex items-center px-4 py-2 bg-destiny-blue text-white rounded hover:bg-opacity-90"
              >
                {t('dashboard.viewAllAchievements')}
              </Link>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-white mb-4">{t('dashboard.yourCharacters')}</h3>
              <p className="text-gray-300 mb-4">{t('dashboard.charactersDescription')}</p>
              <Link
                to="/characters"
                className="inline-flex items-center px-4 py-2 bg-destiny-blue text-white rounded hover:bg-opacity-90"
              >
                {t('dashboard.viewYourCharacters')}
              </Link>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-white mb-4">{t('dashboard.inProgress')}</h3>
              <p className="text-gray-300 mb-4">{t('dashboard.inProgressDescription')}</p>
              <Link
                to="/achievements?progress=1"
                className="inline-flex items-center px-4 py-2 bg-destiny-blue text-white rounded hover:bg-opacity-90"
              >
                {t('dashboard.viewInProgress')}
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;